import { useCallback } from 'react';

import { useFormikContext } from 'formik';

import { AssetItem, RecommendedAsset } from 'config/api/actionLibrary/actionLibrary.types';

import { ActionLibraryInstanceFormValues } from '../../../../ActionLibraryInstanceForm.types';
import { mapRecommendedAssetToItem, mapAvailableAssetToItem } from '../utils/assetMappers';

interface UseAssetSelectionReturn {
  selectedAssets: AssetItem[];
  isSelected: (assetId: number) => boolean;
  toggleRecommended: (asset: RecommendedAsset) => void;
  toggleAvailable: (asset: AssetItem) => void;
  selectAll: (assets: (RecommendedAsset | AssetItem)[]) => void;
  deselectAll: (assets: (RecommendedAsset | AssetItem)[]) => void;
  addAssets: (assets: RecommendedAsset[]) => void;
}

/**
 * Custom hook for managing asset selection state
 */
const useAssetSelection = (): UseAssetSelectionReturn => {
  const { values, setFieldValue } = useFormikContext<ActionLibraryInstanceFormValues>();

  const isSelected = useCallback(
    (assetId: number): boolean => values.selectedAssets.some(a => Number(a.id) === Number(assetId)),
    [values.selectedAssets],
  );

  const toggleRecommended = useCallback(
    (asset: RecommendedAsset) => {
      const assetItem = mapRecommendedAssetToItem(asset);
      const currentIndex = values.selectedAssets.findIndex(a => Number(a.id) === Number(asset.id));
      const newSelected = [...values.selectedAssets];

      if (currentIndex === -1) {
        newSelected.push(assetItem);
      } else {
        newSelected.splice(currentIndex, 1);
      }

      setFieldValue('selectedAssets', newSelected);
    },
    [values.selectedAssets, setFieldValue],
  );

  const toggleAvailable = useCallback(
    (asset: AssetItem) => {
      const assetItem = mapAvailableAssetToItem(asset);
      const currentIndex = values.selectedAssets.findIndex(a => Number(a.id) === Number(asset.id));
      const newSelected = [...values.selectedAssets];

      if (currentIndex === -1) {
        newSelected.push(assetItem);
      } else {
        newSelected.splice(currentIndex, 1);
      }

      setFieldValue('selectedAssets', newSelected);
    },
    [values.selectedAssets, setFieldValue],
  );

  const selectAll = useCallback(
    (assets: (RecommendedAsset | AssetItem)[]) => {
      const newSelected = [...values.selectedAssets];
      assets.forEach(asset => {
        if (!newSelected.find(a => Number(a.id) === Number(asset.id))) {
          const assetItem =
            'type' in asset ? mapRecommendedAssetToItem(asset as RecommendedAsset) : mapAvailableAssetToItem(asset as AssetItem);
          newSelected.push(assetItem);
        }
      });
      setFieldValue('selectedAssets', newSelected);
    },
    [values.selectedAssets, setFieldValue],
  );

  const deselectAll = useCallback(
    (assets: (RecommendedAsset | AssetItem)[]) => {
      const deselectIds = assets.map(a => a.id);
      const newSelected = values.selectedAssets.filter(a => !deselectIds.includes(Number(a.id)));
      setFieldValue('selectedAssets', newSelected);
    },
    [values.selectedAssets, setFieldValue],
  );

  const addAssets = useCallback(
    (assets: RecommendedAsset[]) => {
      const currentSelectedIds = new Set(values.selectedAssets.map(a => Number(a.id)));
      const newRecommendedAssets = assets.filter(asset => !currentSelectedIds.has(asset.id));

      if (newRecommendedAssets.length > 0) {
        const newSelected = [...values.selectedAssets];
        newRecommendedAssets.forEach(asset => {
          newSelected.push(mapRecommendedAssetToItem(asset));
        });
        setFieldValue('selectedAssets', newSelected);
      }
    },
    [values.selectedAssets, setFieldValue],
  );

  return {
    selectedAssets: values.selectedAssets,
    isSelected,
    toggleRecommended,
    toggleAvailable,
    selectAll,
    deselectAll,
    addAssets,
  };
};

export default useAssetSelection;
